import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '@/types/auth.types';
import { STORAGE_KEYS } from '@/utils/constants';
import { LOCAL_ACCESS_TOKEN, LOCAL_OPERATOR, SKIP_AUTH } from '@/config/skipAuth';

interface AuthSliceState {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
}

const storedAccessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
const storedRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
const storedUserRaw = localStorage.getItem(STORAGE_KEYS.USER);
let storedUser: User | null = null;

if (storedUserRaw) {
  try {
    storedUser = JSON.parse(storedUserRaw) as User;
  } catch {
    storedUser = null;
    localStorage.removeItem(STORAGE_KEYS.USER);
  }
}

const hydratedUser = storedAccessToken ? storedUser : null;

const initialState: AuthSliceState = SKIP_AUTH
  ? {
      isAuthenticated: true,
      user: LOCAL_OPERATOR,
      accessToken: LOCAL_ACCESS_TOKEN,
      refreshToken: null,
      loading: false,
      error: null,
    }
  : {
      isAuthenticated: !!storedAccessToken,
      user: hydratedUser,
      accessToken: storedAccessToken,
      refreshToken: storedRefreshToken,
      loading: false,
      error: null,
    };

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; accessToken: string; refreshToken?: string | null }>) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken || null;
      state.loading = false;
      state.error = null;

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, action.payload.accessToken);
      if (action.payload.refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, action.payload.refreshToken);
      } else {
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      }
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(action.payload.user));
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.error = null;
      
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
    },
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      if (state.accessToken) {
        state.isAuthenticated = true;
      }
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(action.payload));
    },
    updateTokens: (state, action: PayloadAction<{ accessToken: string; refreshToken?: string | null }>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken || null;

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, action.payload.accessToken);
      if (action.payload.refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, action.payload.refreshToken);
      } else {
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      }
    },
  },
});

export const {
  setLoading,
  setError,
  loginSuccess,
  logout,
  updateUser,
  updateTokens,
} = authSlice.actions;

export default authSlice.reducer;
